import Alert from "features/shared/alerts/components/Alert";
import React from "react";
import { RequirementTypesMap } from "./requirementFormTypes/RequirementTypesMap";
import { useQuery } from "data/hooks/useQuery";
import TooltipDTO from "features/shared/dtos/TooltipDTO";
import { Tooltips_RequirementType } from "data/endpoints";
import { getQueryParams } from "features/shared/helpers/queryParams";
import DataQuery from "data/DataQuery";
import { EnabledRequirementType } from "../enums/enabledRequirementType";
import { containsKey, isEmpty } from "features/shared/helpers/objects";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import ConditionalExecute from "data/models/ConditionalExecute";
import { useTranslation } from "react-i18next";
import { loadPortalConfig } from "features/shared/helpers/modules";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import RequirementTypesState from "infrastructure/store/requirementTypes/RequirementTypesState";
import DynamicRequirementFormSection from "./requirementFormTypes/dynamicRequirementForm/DynamicRequirementFormSection";
import { containsRequirementType } from "../helpers/requirementTypeHelper";
import GeneralRequirementFormSection from "./requirementFormTypes/generalRequirementForm/GeneralRequirementFormSection";

const portalConfig = loadPortalConfig();

interface Props {
  requirementType: string;
}

const RequirementCreateSection = ({ requirementType }: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );

  // private props
  const loadTooltip: boolean = false;

  // store
  const requirementTypes = useSelector<IAppState, RequirementTypesState>(
    (store) => store.requirementTypes
  ).requiremntTypes;

  // fetch data
  const query: DataQuery = {
    url: Tooltips_RequirementType,
    params: getQueryParams("RequirementType", requirementType),
  };
  const execute: ConditionalExecute = {
    apply: true,
    condition: () => containsRequirementType(requirementType),
  };
  const { data: tooltip } = useQuery<TooltipDTO>(
    query,
    {},
    [loadTooltip],
    { useTracker: true },
    execute
  );

  // sub-renders
  const renderRequirementTypeSection = () => {
    const configType = requirementTypes.find((x) => x.code === requirementType);

    if (configType) {
      // render custom form (if exists custom form renderer)
      if (configType.enabledCustomForm) {
        const sectionToRender = RequirementTypesMap[requirementType];
        if (sectionToRender !== undefined) {
          return sectionToRender({
            useOutsideOfRequirements: !portalConfig.useFullRequirements,
          });
        } // render dynamic form from JSON definition
      } else if (configType.dynamicFormJson) {
        return (
          <DynamicRequirementFormSection
            requirementType={requirementType}
            title={configType.name}
            formJson={configType.dynamicFormJson}
          />
        );
      } else {
        // render general form for full requirements
        return (
          <GeneralRequirementFormSection
            requirementType={requirementType}
            title={configType.name}
          />
        );
      }
    }

    return (
      <Alert
        alertObj={{
          alertType: "Warning",
          message: `"${tLabels(
            "requirementCreateSection.allertMessage"
          )}": "${requirementType}".`,
        }}
        className="m-0"
      />
    );
  };

  const renderTooltip = () => {
    if (!isEmpty(tooltip) && tooltip.htmlText) {
      const renderContent = () => (
        <div
          dangerouslySetInnerHTML={{ __html: tooltip.htmlText || "" }}
          className="m-4"
        />
      );

      return (
        <Alert alertObj={{ alertType: "Info" }} className="mb-4" closable>
          <span>
            {tLabels("requirementCreateSection.moreInfoHowToFillForm")}
          </span>
          <strong
            className="ml-1"
            role="button"
            onClick={() =>
              dialogService.openDialog(
                tLabels("requirementCreateSection.howToFillForm"),
                renderContent
              )
            }
          >
            {tLabels("requirementCreateSection.clickHere")}
          </strong>
          .
        </Alert>
      );
    }
  };

  // render
  return (
    <div>
      {renderTooltip()}
      {renderRequirementTypeSection()}
    </div>
  );
};

export default RequirementCreateSection;
